import axios from 'axios';

// Story Builder is its own backend service, separate from the main portal API —
// set via REACT_APP_STORY_BUILDER_URL (e.g. https://stgstorybuilder.xnotify.ai).
const API_BASE = process.env.REACT_APP_STORY_BUILDER_URL || '';

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  timeout: 120000,
});

api.interceptors.request.use((config) => {
  const apiKey = localStorage.getItem('api-key');
  const accessToken = localStorage.getItem('customer-portal');
  // if (apiKey) config.headers['x-api-key'] = apiKey;
  if (accessToken) config.headers['x-access-token'] = accessToken;
  return config;
});

// Only force-logout when the JWT is genuinely expired (exp claim < now).
// Transient 403s from data endpoints (e.g. a service restart wiping a Redis
// hash, a tile-render permission edge-case) used to wipe the whole session —
// that's what made the portal "time out". Now the route guard handles the
// truly-logged-out case and other 403s just surface as a request error.
function isJwtExpired(token) {
  if (!token || typeof token !== 'string') return false;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (!payload.exp) return false;
    return Date.now() >= payload.exp * 1000;
  } catch (_) { return false; }
}

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 403) {
      const token = localStorage.getItem('customer-portal');
      if (isJwtExpired(token)) {
        localStorage.removeItem('user');
        localStorage.removeItem('customer-portal');
        localStorage.removeItem('api-key');
        window.location.href = '/';
        return new Promise(() => {});
      }
      // Token still valid → 403 is from a specific resource, not the session.
      // Bubble as a normal error so the caller can show a toast or retry.
    }
    const message = err.response?.data?.error || err.message || 'Request failed';
    return Promise.reject(new Error(message));
  }
);

// ── EventSource with auth headers ────────────────────────────
// Native EventSource doesn't support custom headers, so we use
// fetch + ReadableStream and return an EventSource-compatible object.
export function createEventSource(path) {
  const apiKey = localStorage.getItem('api-key');
  const accessToken = localStorage.getItem('customer-portal');
  const url = `${API_BASE}/api${path}`;

  const headers = { Accept: 'text/event-stream', 'Cache-Control': 'no-cache' };
  if (apiKey) headers['x-api-key'] = apiKey;
  if (accessToken) headers['x-access-token'] = accessToken;

  const ctrl = new AbortController();
  const emitter = {
    onmessage: null,
    onerror: null,
    close() { ctrl.abort(); },
  };

  fetch(url, { headers, signal: ctrl.signal })
    .then(async (res) => {
      if (!res.ok) { emitter.onerror?.({ type: 'error' }); return; }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop();
        for (const line of lines) {
          if (line.startsWith('data: ')) emitter.onmessage?.({ data: line.slice(6) });
        }
      }
    })
    .catch((err) => {
      if (err.name !== 'AbortError') emitter.onerror?.({ type: 'error', error: err });
    });

  return emitter;
}

// ── Stories ──────────────────────────────────────────────────

export const storiesApi = {
  list: (page = 1, limit = 20, params = {}) =>
    api.get('/stories', { params: { page, limit, ...params } })
       .then((r) => r.data),

  get: (id) =>
    api.get(`/stories/${id}`).then((r) => r.data.data),

  create: (data) =>
    api.post('/stories', data).then((r) => r.data.data),

  updateMetadata: (id, metadata) =>
    api.put(`/stories/${id}/metadata`, metadata).then((r) => r.data.data),

  delete: (id) =>
    api.delete(`/stories/${id}`).then((r) => r.data),

  duplicate: (id) =>
    api.post(`/stories/${id}/duplicate`).then((r) => r.data.data),

  save: (id, storyData) =>
    api.post(`/stories/${id}/save`, storyData).then((r) => r.data.data),

  // Pages
  addPage: (storyId, afterIndex) =>
    api.post(`/stories/${storyId}/pages`, { afterIndex }).then((r) => r.data.data),

  updatePage: (storyId, pageId, data) =>
    api.put(`/stories/${storyId}/pages/${pageId}`, data).then((r) => r.data.data),

  deletePage: (storyId, pageId) =>
    api.delete(`/stories/${storyId}/pages/${pageId}`).then((r) => r.data),

  reorderPages: (storyId, pageIds) =>
    api.put(`/stories/${storyId}/pages/reorder`, { pageIds }).then((r) => r.data),

  // Layers
  addLayer: (storyId, pageId, layerData) =>
    api.post(`/stories/${storyId}/pages/${pageId}/layers`, layerData).then((r) => r.data.data),

  updateLayer: (storyId, pageId, layerId, data) =>
    api.put(`/stories/${storyId}/pages/${pageId}/layers/${layerId}`, data).then((r) => r.data.data),

  deleteLayer: (storyId, pageId, layerId) =>
    api.delete(`/stories/${storyId}/pages/${pageId}/layers/${layerId}`).then((r) => r.data),

  // Export
  export: (storyId, options) =>
    api.post(`/stories/${storyId}/export`, options)
       .then((r) => r.data.data),

  // Validation
  validate: (storyId) =>
    api.post(`/stories/${storyId}/validate`).then((r) => r.data.data),

  // AI Generation
  aiGenerate: (input) =>
    api.post('/stories/ai-generate', input).then((r) => r.data.data),

  // Publish
  publish: (storyId) =>
    api.post(`/stories/${storyId}/publish`).then((r) => r.data.data),

  // Update page background (live editing in player)
  updatePageBackground: (storyId, pageId, background) =>
    api.patch(`/stories/${storyId}/pages/${pageId}/background`, { background }).then((r) => r.data.data),

  // SDK config
  setMobileAvailability: (storyIds, available) =>
    api.patch('/stories/mobile', { story_ids: storyIds, available }).then((r) => r.data),

  setDefault: (storyId) =>
    api.patch(`/stories/${storyId}/default`).then((r) => r.data),
};

// ── Media ────────────────────────────────────────────────────

export const mediaApi = {
  list: (params) =>
    api.get('/media', { params }).then((r) => r.data.data),

  get: (id) =>
    api.get(`/media/${id}`).then((r) => r.data.data),

  upload: (file, storyId, altText) => {
    const form = new FormData();
    form.append('file', file);
    if (storyId) form.append('storyId', storyId);
    if (altText) form.append('altText', altText);
    return api.post('/media/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then((r) => r.data.data);
  },

  update: (id, data) =>
    api.put(`/media/${id}`, data).then((r) => r.data.data),

  delete: (id) =>
    api.delete(`/media/${id}`).then((r) => r.data),
};

// ── Interactions ─────────────────────────────────────────────

export const interactionsApi = {
  list: (storyId) =>
    api.get(`/stories/${storyId}/interactions`).then((r) => r.data.data),

  get: (id) =>
    api.get(`/interactions/${id}`).then((r) => r.data.data),

  create: (data) =>
    api.post('/interactions', data).then((r) => r.data.data),

  update: (id, data) =>
    api.put(`/interactions/${id}`, data).then((r) => r.data.data),

  respond: (id, optionId) =>
    api.post(`/interactions/${id}/respond`, { optionId }).then((r) => r.data),

  getResults: (id) =>
    api.get(`/interactions/${id}/results`).then((r) => r.data.data),
};

// ── Forms ────────────────────────────────────────────────────

export const formsApi = {
  list: (storyId) =>
    api.get(`/stories/${storyId}/forms`).then((r) => r.data.data),

  get: (id) =>
    api.get(`/forms/${id}`).then((r) => r.data.data),

  create: (data) =>
    api.post('/forms', data).then((r) => r.data.data),

  update: (id, data) =>
    api.put(`/forms/${id}`, data).then((r) => r.data.data),

  delete: (id) =>
    api.delete(`/forms/${id}`).then((r) => r.data),

  getSubmissions: (formId) =>
    api.get(`/forms/${formId}/submissions`).then((r) => r.data.data || []),
};

// ── Templates & Themes ───────────────────────────────────────

export const templatesApi = {
  list: () =>
    api.get('/templates').then((r) => r.data.data),

  themes: () =>
    api.get('/templates/themes').then((r) => r.data.data),

  saveFromStory: (data) =>
    api.post('/templates', data).then((r) => r.data.data),

  delete: (id) =>
    api.delete('/templates/' + id).then((r) => r.data),

  update: (id, data) =>
    api.put('/templates/' + id, data).then((r) => r.data.data),

  createStory: (id, data) =>
    api.post('/templates/' + id + '/create-story', data || {}).then((r) => r.data.data),
};

// ── Unsplash (legacy — kept for compatibility) ───────────────

// Pexels API — primary photo source
export const pexelsApi = {
  search: (query, page = 1, perPage = 24) =>
    api.get('/pexels/photos', {
      params: { query, page, per_page: perPage },
    }).then((r) => r.data.data),

  popular: (page = 1) =>
    api.get('/pexels/photos', {
      params: { page, per_page: 24 },
    }).then((r) => r.data.data),
};

// Keep unsplashApi as alias pointing to Pexels for backward compatibility
export const unsplashApi = pexelsApi;

// ── Giphy ─────────────────────────────────────────────────────

export const giphyApi = {
  searchGifs: (query, page = 1, perPage = 24) =>
    api.get('/giphy/gifs', {
      params: { query, page, per_page: perPage },
    }).then((r) => r.data.data),

  trendingGifs: (page = 1) =>
    api.get('/giphy/gifs', {
      params: { page, per_page: 24 },
    }).then((r) => r.data.data),

  searchStickers: (query, page = 1, perPage = 24) =>
    api.get('/giphy/stickers', {
      params: { query, page, per_page: perPage },
    }).then((r) => r.data.data),

  trendingStickers: (page = 1) =>
    api.get('/giphy/stickers', {
      params: { page, per_page: 24 },
    }).then((r) => r.data.data),
};

// ── Coverr ────────────────────────────────────────────────────

export const coverrApi = {
  search: (query, page = 1, perPage = 20) =>
    api.get('/coverr/videos', {
      params: { query, page, per_page: perPage },
    }).then((r) => r.data.data),

  popular: (page = 1) =>
    api.get('/coverr/videos', {
      params: { page, per_page: 20 },
    }).then((r) => r.data.data),
};

// ── Crawler ──────────────────────────────────────────────────

export const crawlerApi = {
  extract: (url) =>
    api.post('/crawler/extract', { url }).then((r) => r.data.data),
};

// ── Brand ───────────────────────────────────────────────────

export const brandApi = {
  /** Start a crawl job — returns jobId immediately */
  startExtract: (url) =>
    api.post('/brand/extract', { url }).then(r => r.data.data),

  /** Start a refresh job — returns jobId immediately */
  startRefresh: (id) =>
    api.post(`/brand/${id}/refresh`).then(r => r.data.data),

  /** Open an SSE connection for job progress. Returns an EventSource. */
  streamProgress: (jobId) => {
    return createEventSource(`/brand/jobs/${jobId}/progress`);
  },

  /** Poll job status (fallback if SSE not used) */
  getJobStatus: (jobId) =>
    api.get(`/brand/jobs/${jobId}`).then(r => r.data),

  list: () =>
    api.get('/brand').then(r => r.data.data),
  get: (id) =>
    api.get(`/brand/${id}`).then(r => r.data.data),
  update: (id, data) =>
    api.put(`/brand/${id}`, data).then(r => r.data.data),
  delete: (id) =>
    api.delete(`/brand/${id}`).then(r => r.data),
};

// ── Analytics ──────────────────────────────────────────────

export const analyticsApi = {
  getStats: (storyId, from, to) =>
    api.get(`/analytics/stats/${storyId}`, {
      params: { ...(from && { from }), ...(to && { to }) },
    }).then(r => r.data.data),

  getStoryDetails: (storyId) =>
    api.get(`/analytics/story-details/${storyId}`).then(r => r.data.data),

  getEvents: (params) =>
    api.get('/analytics/events', { params }).then(r => r.data.data),
};

// ── Custom Domains (Cloudflare for SaaS) ─────────────────────

export const customDomainsApi = {
  add: (tenantId, hostname) =>
    api.post(`/tenant/${encodeURIComponent(tenantId)}/domains`, { hostname })
       .then(r => r.data.data),

  status: (tenantId, hostnameId) =>
    api.get(`/tenant/${encodeURIComponent(tenantId)}/domains/${hostnameId}/status`)
       .then(r => r.data.data),

  activate: (tenantId, hostnameId, hostname) =>
    api.post(`/tenant/${encodeURIComponent(tenantId)}/domains/${hostnameId}/activate`, { hostname })
       .then(r => r.data),

  remove: (tenantId, hostnameId, hostname) =>
    api.delete(`/tenant/${encodeURIComponent(tenantId)}/domains/${hostnameId}`, { data: { hostname } })
       .then(r => r.data),

  listAll: () =>
    api.get('/tenant/domains').then(r => r.data.data || []),
};

export default api;
